import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  makeStyles,
  Paper,
  Snackbar,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import EditIcon from '@material-ui/icons/Edit';
import ListIcon from '@material-ui/icons/List';
import Alert from '@material-ui/lab/Alert';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { aggregateAndClean, ReleaseNoteRecord } from '../release/promoteUtils';
import {
  Header,
  Page,
  Content,
  InfoCard,
  MarkdownContent,
} from '@backstage/core-components';

const useStyles = makeStyles((theme) => ({
  serviceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 180,
  },
  outputPaper: {
    padding: theme.spacing(3),
    position: 'relative',
  },
  copyButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  previewSection: {
    marginTop: theme.spacing(2),
  },
  rawMarkdown: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    whiteSpace: 'pre-wrap',
    background: theme.palette.type === 'dark' ? '#1e1e1e' : '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: 4,
    overflow: 'auto',
    maxHeight: 500,
  },
}));

interface ServiceSelection {
  id: string;
  service: string;
  fromVersion: string;
  toVersion: string;
  versions: string[];
  loadingVersions: boolean;
}



let idCounter = 0;
function nextId() {
  return `sel-${++idCounter}`;
}

export const ReleaseNoteGeneratorPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const proxyBase = `${backendUrl}/api/proxy/shrek/service-release-notes`;

  const [services, setServices] = useState<string[]>([]);
  const [selections, setSelections] = useState<ServiceSelection[]>([
    { id: nextId(), service: '', fromVersion: '', toVersion: '', versions: [], loadingVersions: false },
  ]);
  const [output, setOutput] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch available services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch(`${proxyBase}`);
        if (res.ok) {
          const data: ReleaseNoteRecord[] = await res.json();
          const uniqueServices = [...new Set(data.map((d) => d.service))].sort();
          setServices(uniqueServices);
        }
      } catch {
        // ignore
      }
    };
    fetchServices();
  }, [fetch, proxyBase]);

  // Fetch versions when a service is selected
  const handleServiceChange = useCallback(
    async (selectionId: string, service: string) => {
      setSelections((prev) =>
        prev.map((s) =>
          s.id === selectionId
            ? { ...s, service, fromVersion: '', toVersion: '', versions: [], loadingVersions: true }
            : s,
        ),
      );

      try {
        const res = await fetch(`${proxyBase}/${service}/versions`);
        if (res.ok) {
          const versions: string[] = await res.json();
          setSelections((prev) =>
            prev.map((s) =>
              s.id === selectionId ? { ...s, versions, loadingVersions: false } : s,
            ),
          );
        }
      } catch {
        setSelections((prev) =>
          prev.map((s) =>
            s.id === selectionId ? { ...s, loadingVersions: false } : s,
          ),
        );
      }
    },
    [fetch, proxyBase],
  );

  const addSelection = () => {
    setSelections((prev) => [
      ...prev,
      { id: nextId(), service: '', fromVersion: '', toVersion: '', versions: [], loadingVersions: false },
    ]);
  };

  const removeSelection = (id: string) => {
    setSelections((prev) => prev.filter((s) => s.id !== id));
  };

  const updateSelection = (id: string, field: 'fromVersion' | 'toVersion', value: string) => {
    setSelections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const canGenerate = selections.every(
    (s) => s.service && s.fromVersion && s.toVersion,
  ) && selections.length > 0;

  const generate = async () => {
    setGenerating(true);
    setOutput(null);

    try {
      const perService = await Promise.all(
        selections.map(async (sel) => {
          const res = await fetch(
            `${proxyBase}/${sel.service}/range?from=${encodeURIComponent(sel.fromVersion)}&to=${encodeURIComponent(sel.toVersion)}`,
          );
          const notes: ReleaseNoteRecord[] = res.ok ? await res.json() : [];
          return { service: sel.service, notes };
        }),
      );

      const merged = await aggregateAndClean(perService, fetch, backendUrl);
      setOutput(merged);
    } catch {
      setOutput('Error generating release notes.');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (output) {
      await navigator.clipboard.writeText(output);
      setCopied(true);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Release Note Generator" subtitle="Combine release notes across services into a single customer-facing document" />
      <Content>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="outlined"
            startIcon={<ListIcon />}
            onClick={() => navigate('/release-notes/versions')}
          >
            Manage Versions
          </Button>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <InfoCard title="Select Services & Version Ranges">
              {selections.map((sel) => (
                <Box key={sel.id} className={classes.serviceRow}>
                  <FormControl variant="outlined" size="small" className={classes.formControl}>
                    <InputLabel>Service</InputLabel>
                    <Select
                      value={sel.service}
                      onChange={(e) => handleServiceChange(sel.id, e.target.value as string)}
                      label="Service"
                    >
                      {services.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined" size="small" className={classes.formControl} disabled={!sel.service || sel.loadingVersions}>
                    <InputLabel>From</InputLabel>
                    <Select
                      value={sel.fromVersion}
                      onChange={(e) => updateSelection(sel.id, 'fromVersion', e.target.value as string)}
                      label="From"
                    >
                      {sel.versions.map((v) => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined" size="small" className={classes.formControl} disabled={!sel.service || sel.loadingVersions}>
                    <InputLabel>To</InputLabel>
                    <Select
                      value={sel.toVersion}
                      onChange={(e) => updateSelection(sel.id, 'toVersion', e.target.value as string)}
                      label="To"
                    >
                      {sel.versions.map((v) => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {sel.loadingVersions && <CircularProgress size={20} />}

                  {selections.length > 1 && (
                    <IconButton size="small" onClick={() => removeSelection(sel.id)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}

              <Box display="flex" gridGap={16} mt={1}>
                <Button size="small" startIcon={<AddIcon />} onClick={addSelection}>
                  Add Service
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={generate}
                  disabled={!canGenerate || generating}
                >
                  {generating ? <CircularProgress size={20} /> : 'Generate'}
                </Button>
              </Box>
            </InfoCard>
          </Grid>

          {output && (
            <Grid item xs={12}>
              <Paper className={classes.outputPaper}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {showRaw ? 'Markdown' : 'Preview'}
                  </Typography>
                  <Box>
                    <Button size="small" onClick={() => setShowRaw((v) => !v)}>
                      {showRaw ? 'Show Preview' : 'Show Markdown'}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<FileCopyIcon />}
                      onClick={copyToClipboard}
                    >
                      Copy
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<EditIcon />}
                      onClick={() => navigate('/release-notes/prepare', { state: { markdown: output } })}
                    >
                      Prepare
                    </Button>
                  </Box>
                </Box>

                {showRaw ? (
                  <div className={classes.rawMarkdown}>{output}</div>
                ) : (
                  <MarkdownContent content={output} />
                )}
              </Paper>
            </Grid>
          )}
        </Grid>

        <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)}>
          <Alert severity="success" onClose={() => setCopied(false)}>
            Copied to clipboard
          </Alert>
        </Snackbar>

      </Content>
    </Page>
  );
};
